import React, { useState } from 'react';
import { Barcode, X, Check, Loader } from 'lucide-react';
import { useTranslation } from '../../contexts/I18nProvider';
import { itemUnitsService } from '../../services/itemUnitsService';

/**
 * «Legg til strekkode»-dialog (TW 36320099, flyttet til shared i TW 36376015).
 *
 * Vises når en skannet strekkode ikke finnes i Items eller ItemUnits. Operatøren velger
 * hvilken av ordrens varer koden tilhører, og koden lagres som ItemUnits-rad — lokalt i
 * Compass, utenfor eSENTRAL-importens rekkevidde. Backend normaliserer (GTIN-13/14) og
 * håndhever unikhet på tvers av Items + ItemUnits.
 *
 * Brukes av varemottaket (POrderReceivingDetail) og vareplassering (ModernPutAwayDetails).
 * `lines` er den aktuelle ordrens linjer — hvilken ordretype spiller ingen rolle, kun at
 * hver linje har itemNo (+ valgfritt description1/itemUnitCode).
 */

interface AddBarcodeLineOption {
  itemNo: string;
  description1?: string | null;
  itemUnitCode?: string | null;
}

interface AddBarcodeModalProps {
  isOpen: boolean;
  scannedBarcode: string;
  /** Ordrens linjer — operatøren velger varen koden skal registreres på. */
  lines: AddBarcodeLineOption[];
  companyNo: string;
  onClose: () => void;
  /** Kalles etter vellykket registrering, med varenummeret koden ble lagt på. */
  onCreated: (itemNo: string) => void;
}

export const AddBarcodeModal: React.FC<AddBarcodeModalProps> = ({
  isOpen,
  scannedBarcode,
  lines,
  companyNo,
  onClose,
  onCreated
}) => {
  const { t } = useTranslation();
  const [selectedItemNo, setSelectedItemNo] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  // Dedupliser på varenummer (en ordre kan ha flere linjer av samme vare)
  const uniqueItems = Array.from(
    new Map(lines.filter(l => l.itemNo).map(l => [l.itemNo, l])).values()
  );

  const handleConfirm = async () => {
    if (!selectedItemNo) return;
    const selected = uniqueItems.find(i => i.itemNo === selectedItemNo);
    if (!selected) return;

    setSaving(true);
    setError(null);
    try {
      await itemUnitsService.createItemUnit({
        companyNo,
        itemNo: selected.itemNo,
        // Tenant-konvensjon: enhetskoden fra linja, fallback '1' (som eksisterende ItemUnits-rader)
        itemUnitCode: selected.itemUnitCode || '1',
        itemUnitTxt: null,
        itemBarCode: scannedBarcode,
        unitFactor: 1
      });
      onCreated(selected.itemNo);
    } catch (err) {
      // 409 fra backend bærer forklarende melding (koden finnes allerede på en annen vare)
      setError(err instanceof Error ? err.message : t('common.errors.saveFailed', {}, 'Kunne ikke lagre'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[85vh] flex flex-col">
        <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Barcode size={24} />
            <h2 className="text-xl font-semibold">
              {t('warehouse.modules.receipt.detail.addBarcodeTitle', {}, 'Registrer ny strekkode')}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-blue-700 rounded"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <p className="text-sm text-gray-700 mb-2">
            {t('warehouse.modules.receipt.detail.addBarcodeScannedCode', {}, 'Skannet kode (ukjent):')}
          </p>
          <div className="font-mono text-lg font-semibold bg-gray-100 rounded-lg px-3 py-2 mb-4 break-all">
            {scannedBarcode}
          </div>

          <p className="text-sm text-gray-700 mb-2">
            {t('warehouse.modules.receipt.detail.addBarcodeSelectItem', {}, 'Velg varen koden tilhører:')}
          </p>
          <div className="space-y-2 mb-4">
            {uniqueItems.map(item => (
              <button
                key={item.itemNo}
                onClick={() => setSelectedItemNo(item.itemNo)}
                disabled={saving}
                className={`w-full text-left px-3 py-3 min-h-[44px] rounded-lg border transition-colors ${
                  selectedItemNo === item.itemNo
                    ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-300'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  {selectedItemNo === item.itemNo && <Check size={16} className="text-blue-600 flex-shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-gray-900">{item.itemNo}</div>
                    {item.description1 && (
                      <div className="text-xs text-gray-600 truncate">{item.description1}</div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            {t('warehouse.modules.receipt.detail.addBarcodeInfo', {}, 'Strekkoden lagres lokalt i Compass og overskrives ikke av vareimporten. Neste skann av koden gir treff på varen.')}
          </div>

          {error && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 break-words">
              {error}
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 min-h-[44px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            {t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || !selectedItemNo}
            className="px-4 py-2 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            {saving ? <Loader size={16} className="animate-spin" /> : <Barcode size={16} />}
            <span>{saving
              ? t('warehouse.modules.receipt.detail.addBarcodeSaving', {}, 'Lagrer...')
              : t('warehouse.modules.receipt.detail.addBarcodeConfirmButton', {}, 'Registrer strekkode')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
